import type { KollaProvidersEntityOrResourcesEntity } from 'app/services/kolla'
import { withZod } from '@remix-validated-form/with-zod'
import { graphql } from 'app/generated/gql'
import { uniqBy } from 'lodash'
import { useMemo, useState } from 'react'
import { useMutation } from 'urql'
import { z } from 'zod'
import { zfd } from 'zod-form-data'
import Button from '../Button'
import FormCombo from '../form/FormCombo'
import FormComboMultiSelect from '../form/FormComboMultiSelect'
import FormHiddenInput from '../form/FormHiddenInput'
import { SmartForm } from '../form/SmartForm'
import { EmptySearch } from '../shared/styled/EmptySearch'
import { Info } from '../Info'
import { Toggle } from '../Toggle'
import { AppointmentTypeJson } from 'app/types/entities'
import type { AvailabilityType } from 'app/types/entities'
import type { OpenHours } from 'app/types/entities'
import { ProviderType } from 'app/types/entities'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { OptionType } from 'types/OptionType'
import OfficeHoursEditor from 'app/components/form/OfficeHoursEditor'
import { resolveInitialWeeklyHours } from './availabilityHours.utils'

export const CREATE_AVAILABILITY_MUTATION = graphql(`
  mutation CreateAvailability($input: CreateAvailabilityInput!) {
    createAvailability(input: $input) {
      id
      orgId
      workspaceId
      locationId
      kollaConsumerId
      providerId
      archived
      operatoryId
      operatoryIds
      appointmentTypeIds
      backupAvailability
    }
  }
`)

export const UPDATE_AVAILABILITY_MUTATION = graphql(`
  mutation UpdateAvailability($id: ID!, $input: UpdateAvailabilityInput!) {
    updateAvailability(id: $id, input: $input) {
      id
      orgId
      workspaceId
      locationId
      kollaConsumerId
      providerId
      archived
      operatoryId
      operatoryIds
      appointmentTypeIds
      backupAvailability
    }
  }
`)

/** Extracts operatory number from name (resources/operatory_6 -> 6) or remote_id (OP-6 -> 6, OP6 -> 6) */
function getOperatoryNumber(op: KollaProvidersEntityOrResourcesEntity): string {
  // Try extracting from name field (resources/operatory_6)
  const nameMatch = op.name?.match(/operatory_(\d+)/)
  if (nameMatch) return nameMatch[1]

  // Try extracting from remote_id (OP-6 or OP6)
  const remoteMatch = op.remote_id?.match(/^OP-?(\d+)$/i)
  if (remoteMatch) return remoteMatch[1]

  return ``
}

/** Formats operatory display name with number prefix and shortcode, e.g., "6 - Dr. Ben (FORD)" */
function formatOperatoryName(op: KollaProvidersEntityOrResourcesEntity): string {
  const num = getOperatoryNumber(op)
  const shortcode = op.additional_data?.short_display_name
  const base = num ? `${num} - ${op.display_name}` : op.display_name
  return shortcode ? `${base} (${shortcode})` : base
}

const createSchema = z.object({
  operatoryId: z.string().min(1, { message: `An operatory is required` }),
  appointmentTypeIds: z
    .string()
    .transform((val) => (val === `` ? [] : val.split(`,`)))
    .pipe(z.array(z.string()).min(1, `Must choose at least one appointment type`)),
  backupAvailability: z.preprocess((val) => val === `true` || val === true, z.boolean()).optional(),

  timeSlots: zfd
    .json(
      z.object({
        sunday: z
          .object({
            hours: z
              .object({
                from: z.string(),
                to: z.string(),
              })
              .array(),
            open: z.boolean(),
          })
          .optional(),
        monday: z
          .object({
            hours: z
              .object({
                from: z.string(),
                to: z.string(),
              })
              .array(),
            open: z.boolean(),
          })
          .optional(),
        tuesday: z
          .object({
            hours: z
              .object({
                from: z.string(),
                to: z.string(),
              })
              .array(),
            open: z.boolean(),
          })
          .optional(),
        wednesday: z
          .object({
            hours: z
              .object({
                from: z.string(),
                to: z.string(),
              })
              .array(),
            open: z.boolean(),
          })
          .optional(),
        thursday: z
          .object({
            hours: z
              .object({
                from: z.string(),
                to: z.string(),
              })
              .array(),
            open: z.boolean(),
          })
          .optional(),
        friday: z
          .object({
            hours: z
              .object({
                from: z.string(),
                to: z.string(),
              })
              .array(),
            open: z.boolean(),
          })
          .optional(),
        saturday: z
          .object({
            hours: z
              .object({
                from: z.string(),
                to: z.string(),
              })
              .array(),
            open: z.boolean(),
          })
          .optional(),
      }),
    )
    .optional(),
})

export const createValidator = withZod(createSchema)

const createSchemaV2 = createSchema.omit({ operatoryId: true }).extend({
  operatoryIds: z
    .array(z.object({ value: z.string(), id: z.string(), name: z.string() }).transform((o) => o.value))
    .min(1, `Must choose at least one operatory`),
})

export const createValidatorV2 = withZod(createSchemaV2)

// Standard mode (Kolla locations) sources hours and operatories from the PMS, so an
// availability may be saved with only a provider and appointment types.
const createSchemaV2Standard = createSchema.omit({ operatoryId: true }).extend({
  operatoryIds: z
    .array(z.object({ value: z.string(), id: z.string(), name: z.string() }).transform((o) => o.value))
    .optional()
    .default([]),
})

export const createValidatorV2Standard = withZod(createSchemaV2Standard)

const editSchema = createSchema.extend({ id: z.string().min(1, { message: `An availability id is required` }) })

export const editValidator = withZod(editSchema)

const editSchemaV2 = createSchemaV2.extend({ id: z.string().min(1, { message: `An availability id is required` }) })

export const editValidatorV2 = withZod(editSchemaV2)

const editSchemaV2Standard = createSchemaV2Standard.extend({
  id: z.string().min(1, { message: `An availability id is required` }),
})

export const editValidatorV2Standard = withZod(editSchemaV2Standard)

export function CreateAvailabilityModal({
  onAvailabilityCreatedOrUpdated,
  editingAvailability,
  onAvailabilityDelete,
  operatories,
  appointmentTypes,
  provider,
  isV2,
  standardMode,
  locationOpenHours,
}: {
  onAvailabilityCreatedOrUpdated: (availability?: AvailabilityType) => void
  editingAvailability?: AvailabilityType
  onAvailabilityDelete?: (availabilityId: string) => Promise<void>
  operatories: KollaProvidersEntityOrResourcesEntity[]
  appointmentTypes?: AppointmentTypeJson[]
  provider: ProviderType
  isV2?: boolean
  standardMode?: boolean
  locationOpenHours?: OpenHours
}) {
  // Deduplicate operatories by remote_id
  const uniqueOperatories = useMemo(() => uniqBy(operatories, `remote_id`), [operatories])

  const [selectedOperatory, setSelectedOperatory] = useState<OptionType | undefined>(() => {
    if (!editingAvailability) return undefined
    const op = uniqueOperatories?.find((op) => op.remote_id === editingAvailability.operatoryId)
    if (!op) return undefined
    return {
      id: editingAvailability.operatoryId!,
      name: formatOperatoryName(op),
      value: editingAvailability.operatoryId!,
    }
  })
  const [selectedOperatories, setSelectedOperatories] = useState<OptionType[]>(() => {
    if (!editingAvailability) return []
    const ids = editingAvailability.operatoryIds?.length
      ? editingAvailability.operatoryIds
      : editingAvailability.operatoryId
        ? [editingAvailability.operatoryId]
        : []
    return ids
      .map((opId) => {
        const op = uniqueOperatories?.find((o) => o.remote_id === opId)
        if (!op) return undefined
        return { id: opId, name: formatOperatoryName(op), value: opId }
      })
      .filter((o): o is OptionType => !!o)
  })
  const [weeklyHours, setWeeklyHours] = useState<OpenHours>(() =>
    resolveInitialWeeklyHours(editingAvailability, locationOpenHours),
  )
  const [selectedAppointmentTypeIds, setSelectedAppointmentTypeIds] = useState<string[]>(
    editingAvailability?.appointmentTypeIds ?? [],
  )
  const [backupAvailability, setBackupAvailability] = useState<boolean>(
    editingAvailability?.backupAvailability ?? false,
  )
  const [isDeleting, setIsDeleting] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [, executeCreateAvailability] = useMutation(CREATE_AVAILABILITY_MUTATION)
  const [, executeUpdateAvailability] = useMutation(UPDATE_AVAILABILITY_MUTATION)

  const onFormSubmit = async (data: any) => {
    setIsSaving(true)
    const operatoryIds: string[] = isV2 ? (data.operatoryIds ?? []) : data.operatoryId ? [data.operatoryId] : []
    const result = editingAvailability
      ? await executeUpdateAvailability({
          id: editingAvailability.id!,
          input: {
            operatoryIds,
            appointmentTypeIds: data.appointmentTypeIds,
            timeSlots: data.timeSlots ?? {},
            backupAvailability: data.backupAvailability,
          },
        })
      : await executeCreateAvailability({
          input: {
            providerId: provider.id!,
            operatoryIds,
            appointmentTypeIds: data.appointmentTypeIds,
            timeSlots: data.timeSlots ?? {},
            backupAvailability: data.backupAvailability,
          },
        })
    setIsSaving(false)
    if (result.error) {
      notifyError(`Failed to ${editingAvailability ? `save` : `create`} availability: ${result.error.message}`)
    } else {
      notifySuccess(`Availability ${editingAvailability ? `saved` : `created`}`)
      closeModal()
      const saved = editingAvailability
        ? (result.data as { updateAvailability?: unknown } | undefined)?.updateAvailability
        : (result.data as { createAvailability?: unknown } | undefined)?.createAvailability
      onAvailabilityCreatedOrUpdated(saved as AvailabilityType | undefined)
    }
  }

  const onAppointmentTypeToggle = (appointmentTypeId: string, isActive: boolean) => {
    if (isActive) {
      setSelectedAppointmentTypeIds((prev) => [...prev, appointmentTypeId])
    } else {
      setSelectedAppointmentTypeIds((prev) => prev.filter((id) => id !== appointmentTypeId))
    }
  }

  return (
    <div className="flex w-[960px] max-w-[90vw] flex-col">
      <SmartForm
        className="!mt-0 max-w-none"
        onCancel={() => closeModal()}
        validator={
          (isV2
            ? editingAvailability
              ? standardMode
                ? editValidatorV2Standard
                : editValidatorV2
              : standardMode
                ? createValidatorV2Standard
                : createValidatorV2
            : editingAvailability
              ? editValidator
              : createValidator) as any
        }
        hiddenInputs={editingAvailability ? [{ name: `id`, value: editingAvailability.id }] : []}
        reloadDocument={false}
        preventSubmit
        onSubmit={onFormSubmit}
        hideButtons
      >
        <FormHiddenInput name="appointmentTypeIds" value={selectedAppointmentTypeIds} />
        <FormHiddenInput name="timeSlots" value={JSON.stringify(weeklyHours)} />
        <FormHiddenInput name="backupAvailability" value={String(backupAvailability)} />
        <div className="col-span-6 w-full">
          <div className="col-span-6 grid grid-cols-6">
            <div className="col-span-4 grid grid-cols-6 content-start border-r border-default px-6 pt-4">
              {isV2 ? (
                <FormComboMultiSelect
                  cols={6}
                  name="operatoryIds"
                  label="Operatories"
                  placeholder="Select operatories"
                  options={uniqueOperatories.map((op) => ({
                    id: op.remote_id,
                    name: formatOperatoryName(op),
                    value: op.remote_id,
                  }))}
                  values={selectedOperatories}
                  onChange={(options) => setSelectedOperatories(options)}
                  labelClassName="text-body-sm text-strong mb-1.5"
                />
              ) : (
                <FormCombo
                  cols={6}
                  name="operatoryId"
                  label="Operatory"
                  placeholder="Select operatory"
                  options={uniqueOperatories.map((op) => ({
                    id: op.remote_id,
                    name: formatOperatoryName(op),
                    value: op.remote_id,
                  }))}
                  value={selectedOperatory}
                  onChange={(option) => setSelectedOperatory(option)}
                  labelClassName="text-body-sm text-strong mb-1.5"
                />
              )}
              <div className="col-span-6 my-6">
                <OfficeHoursEditor
                  value={weeklyHours}
                  onChange={setWeeklyHours}
                  seedHours={locationOpenHours}
                  variant="panel"
                  title="Weekly hours"
                  compact={true}
                />
              </div>
            </div>
            <div className="col-span-2 px-4 pt-4">
              <p className="text-body-sm text-strong">Settings</p>
              <div className="mb-6 mt-4 flex flex-col gap-y-5 pb-2.5">
                <div className="flex items-center gap-2">
                  <Toggle
                    label="Backup Availability"
                    defaultValue={backupAvailability}
                    handleToggle={(isActive) => setBackupAvailability(isActive)}
                  />
                  <Info
                    title="Backup Availability"
                    message="When on, this availability is held back and not offered on the first get_availability tool call."
                  />
                </div>
              </div>
              <p className="text-body-sm text-strong">Appointment types</p>
              <div className="mt-4 flex flex-col gap-y-5 pb-2.5">
                {appointmentTypes == null || appointmentTypes.length === 0 ? (
                  <EmptySearch
                    icon={() => <></>}
                    className="mt-14 px-14 text-center"
                    title="No Appointment Types Enabled"
                    description="Please enable at least one appointment in the “Appointment Type” tab before proceeding. "
                    titleSize="sm"
                  />
                ) : (
                  appointmentTypes.map((appointmentType) => (
                    <div key={appointmentType.vendorId}>
                      <Toggle
                        label={appointmentType.displayName}
                        defaultValue={editingAvailability?.appointmentTypeIds?.includes(appointmentType.vendorId!)}
                        handleToggle={(isActive) => onAppointmentTypeToggle(appointmentType.vendorId!, isActive)}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div
            className={`sticky bottom-0 z-10 col-span-6 flex shrink-0 rounded-b-lg border-t p-3 ${
              editingAvailability ? `justify-between` : `justify-end`
            } bg-surface`}
          >
            {!!editingAvailability && (
              <Button
                label="Delete"
                size="md"
                mode="danger"
                loading={isDeleting}
                onClick={async () => {
                  setIsDeleting(true)
                  if (onAvailabilityDelete) {
                    await onAvailabilityDelete(editingAvailability.id!)
                  }
                }}
              />
            )}
            <div className="flex justify-end gap-4">
              <Button label="Cancel" mode="tertiary" size="md" onClick={() => closeModal()} />
              <Button
                type="submit"
                label="Save"
                mode="primary"
                loading={isSaving}
                disabled={
                  (standardMode ? false : isV2 ? !selectedOperatories.length : !selectedOperatory) ||
                  selectedAppointmentTypeIds.length === 0
                }
                size="md"
              />
            </div>
          </div>
        </div>
      </SmartForm>
    </div>
  )
}
